import type { User } from "../types";
import {
  USER_KEY,
  LANGUAGE_KEY,
  EMBED_VIEW_KEY,
  HIDE_COMMENTS_KEY,
  EXIT_IFRAME_KEY
} from "../constants/userStorageConstants";
import {
  getUserQueryData,
  removeUserPermissionsQuery,
  setUserQuery
} from "@certa/queries/hooks/authV2.hooks";
import { tenant } from "@certa/network/src/config";

export type UserData = User & {
  preferredLanguage: string;
  [key: string]: any;
};

function setUser(user: Partial<UserData>): void {
  try {
    setUserQuery(user);

    const userWithCsrf = {
      ...user,
      tenant: user.tenant || tenant,
      csrf: document.cookie
    };
    localStorage.setItem(USER_KEY, JSON.stringify(userWithCsrf));
  } catch (error) {
    console.error("Failed to store user data:", error);
  }
}

function getUser(): UserData | null {
  try {
    const cachedUser = getUserQueryData();
    if (cachedUser) {
      const storedUser = localStorage.getItem(USER_KEY);
      if (!storedUser) {
        localStorage.setItem(USER_KEY, JSON.stringify(cachedUser));
      }
      return cachedUser;
    }

    const storedUser = localStorage.getItem(USER_KEY);
    if (storedUser) {
      const user = JSON.parse(storedUser);
      setUserQuery(user);
      return user;
    }

    return null;
  } catch (error) {
    console.error("Failed to retrieve user data:", error);
    return null;
  }
}

function clearUser(): void {
  try {
    removeUserPermissionsQuery();
    localStorage.removeItem(USER_KEY);
  } catch (error) {
    console.error("Failed to clear user data:", error);
  }
}

function setLanguage(language: string): void {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);

    const user = getUser();
    if (user) {
      setUser({ ...user, preferredLanguage: language });
    }
  } catch (error) {
    console.error("Failed to store language preference:", error);
  }
}

function getLanguage(): string | null {
  try {
    return localStorage.getItem(LANGUAGE_KEY);
  } catch (error) {
    console.error("Failed to retrieve language preference:", error);
    return null;
  }
}

function setEmbedViewState(state: {
  isEmbeddedView?: boolean;
  hideCommentsIcon?: boolean;
  exitIframeButton?: boolean;
}): void {
  try {
    if (state.isEmbeddedView !== undefined) {
      localStorage.setItem(EMBED_VIEW_KEY, state.isEmbeddedView.toString());
    }
    if (state.hideCommentsIcon !== undefined) {
      localStorage.setItem(
        HIDE_COMMENTS_KEY,
        state.hideCommentsIcon.toString()
      );
    }
    if (state.exitIframeButton !== undefined) {
      localStorage.setItem(EXIT_IFRAME_KEY, state.exitIframeButton.toString());
    }
  } catch (error) {
    console.error("Failed to store embed view state:", error);
  }
}

function getCSRFToken(): string | null {
  const user = getUser();
  return user?.csrf || null;
}

function updateUserPreferredLanguage(language: string) {
  const user = getUser();
  if (user) {
    setUser({ ...user, prefered_language: language });
  }
}

export const UserStorage = {
  setUser,
  getUser,
  clearUser,
  setLanguage,
  getLanguage,
  setEmbedViewState,
  getCSRFToken,
  updateUserPreferredLanguage
};
